import { StyleShadowRoot } from "@redotech/react-util/style";
import {
  InsertionPosition,
  WidgetAutocheck,
} from "@redotech/redo-model/widget";
import { ModalRootContext } from "@redotech/redo-web/modal-root";
import { assertNever } from "@redotech/util/type";
import { createRoot } from "react-dom/client";
import { StorefrontClient } from "../client/storefront";
import { log } from "../monitoring/log";
import { reportTiming } from "../monitoring/timing";
import { RedoRoot } from "../root";
import STYLE_MANAGER from "../style-manager";
import { getDomObserver } from "../utils/dom";
import { getPlacedCustomElement } from "../utils/element-placement";
import { TryFirstProvider } from "./context";
import { getPreviewModeAndStoreInLocalStorage } from "./preview-mode";
import { TryFirstButton } from "./try-first-button";
import { TryFirstContainer } from "./try-first-container";

const MAX_SELECTOR_ELEMENTS = 51;

export function isTryFirstSetup(autocheck: WidgetAutocheck) {
  return autocheck.tryFirst?.sellingPlanId;
}

function isDescendantOf(element: Element, possibleAncestor: Element) {
  let currentElement: Element | null = element;
  while (currentElement) {
    if (currentElement === possibleAncestor) {
      return true;
    }
    currentElement = currentElement.parentElement;
  }
  return false;
}

function createTryFirstWidget(
  element: Element,
  autocheck: WidgetAutocheck,
  extensionShopifyClient: StorefrontClient,
  insertionType: InsertionPosition = InsertionPosition.APPEND,
) {
  const tryFirstContainer = document.createElement(
    TryFirstContainer.TAG,
  ) as TryFirstContainer;

  switch (insertionType) {
    case InsertionPosition.BEFORE:
      element.before(tryFirstContainer);
      break;
    case InsertionPosition.AFTER:
      element.after(tryFirstContainer);
      break;
    case InsertionPosition.PREPEND:
      element.prepend(tryFirstContainer);
      break;
    case InsertionPosition.APPEND:
      element.append(tryFirstContainer);
      break;
    default:
      console.warn(`Unknown insertion position: ${insertionType}`);
      assertNever(insertionType);
      return;
  }

  // Use requestAnimationFrame to ensure connectedCallback has run
  requestAnimationFrame(() => {
    const root = createRoot(tryFirstContainer.root as ShadowRoot);

    const storeUrl =
      window.redoStorefront?.storeUrl ??
      window.Shopify?.shop ??
      window.Shopify?.Checkout?.apiHost;

    root.render(
      <StyleShadowRoot
        shadowRoot={tryFirstContainer.root as ShadowRoot}
        styleManager={STYLE_MANAGER}
      >
        <RedoRoot
          extensionShopifyClient={extensionShopifyClient}
          storeUrl={storeUrl}
        >
          <ModalRootContext.Provider value={tryFirstContainer.modalContainer}>
            <TryFirstProvider
              autocheck={autocheck}
              container={tryFirstContainer}
              extensionShopifyClient={extensionShopifyClient}
            >
              <TryFirstButton />
            </TryFirstProvider>
          </ModalRootContext.Provider>
        </RedoRoot>
      </StyleShadowRoot>,
    );

    reportTiming("tryFirst.contextProviderAdded");
  });
}

function placeTryFirstWidgetsOnDivs(
  autocheck: WidgetAutocheck,
  extensionShopifyClient: StorefrontClient,
) {
  const elements = document.querySelectorAll(".redo-try-first-button");

  for (const element of elements) {
    const existingContainer = getPlacedCustomElement(
      TryFirstContainer.TAG,
      element,
      InsertionPosition.APPEND,
    );

    if (existingContainer) {
      continue;
    }

    // Shopify applies display: none to empty divs
    (element as HTMLElement).style.display = "unset";

    createTryFirstWidget(element, autocheck, extensionShopifyClient);
  }
}

function placeTryFirstWidgetsOnCssSelectors(
  autocheck: WidgetAutocheck,
  extensionShopifyClient: StorefrontClient,
) {
  const placement = autocheck.tryFirst?.widget?.placement;
  if (!placement?.button?.selector || !placement?.button?.placementType) {
    console.warn(
      "TRY FIRST PLACEMENT: No CSS selector or placement type found",
    );
    return;
  }

  console.log(
    `TRY FIRST PLACEMENT: Placing Try First on CSS selector: ${placement.button.selector}`,
  );

  const { selector, placementType } = placement.button;

  const allMatchedElements = document.querySelectorAll(selector);

  const elements = Array.from(allMatchedElements).filter(
    (elementToValidate) => {
      // Make sure we don't place a Try First container inside of another Try First container (could create infinite loop)
      for (const possibleAncestor of allMatchedElements) {
        if (elementToValidate === possibleAncestor) {
          continue;
        }

        if (isDescendantOf(elementToValidate, possibleAncestor)) {
          return false;
        }
      }
      return true;
    },
  );

  if (elements.length > MAX_SELECTOR_ELEMENTS) {
    log(
      `TRY FIRST PLACEMENT: Too many elements found for CSS selector "${selector}" (${elements.length} > 51). Aborting CSS placement.`,
    );
    return;
  }

  for (const element of elements) {
    const existingContainer = getPlacedCustomElement(
      TryFirstContainer.TAG,
      element,
      placementType,
    );

    if (existingContainer) {
      console.log(
        "TRY FIRST PLACEMENT: Skipping element because it already has a Try First container",
        element,
      );
      continue;
    }

    console.log(
      "TRY FIRST PLACEMENT: Placing Try First container on element",
      element,
    );

    createTryFirstWidget(
      element,
      autocheck,
      extensionShopifyClient,
      placementType,
    );
  }
}

function placeTryFirstWidgets(
  autocheck: WidgetAutocheck,
  extensionShopifyClient: StorefrontClient,
) {
  placeTryFirstWidgetsOnDivs(autocheck, extensionShopifyClient);
  placeTryFirstWidgetsOnCssSelectors(autocheck, extensionShopifyClient);
}

export function initialize(
  autocheck: WidgetAutocheck,
  extensionShopifyClient: StorefrontClient,
) {
  log("Initializing Try First...");

  // Always set localStorage for try first preview mode during initialization
  getPreviewModeAndStoreInLocalStorage();

  TryFirstContainer.register();

  const domObserver = getDomObserver();
  const placementCallback = () => {
    placeTryFirstWidgets(autocheck, extensionShopifyClient);
  };

  domObserver.registerCallback(placementCallback);

  placeTryFirstWidgets(autocheck, extensionShopifyClient);
}
